// Wallet card palette — the TypeScript mirror of
// backend/internal/wallet/palette.go.
//
// The admin preview has to predict what Apple and Google actually render, and
// the pass artwork is drawn server-side in Go. There is no shared runtime, so
// this file is a hand-maintained replica and the two WILL drift unless they
// are changed together. walletCardPalette.test.ts pins the values that matter.
//
// Keep in step with:
//   - derivePalette / LegacyCardColors  (palette.go)
//   - heroScrimAlpha* / coinShadowAlpha* (passimage.go)

export interface CardColors {
  background: string
  text: string
  circles: string
  /** The loyalty page accent — the fallback each blank field derives from. */
  legacyAccent: string
}

export interface CardPalette {
  bg: string
  text: string
  circles: string
  panel: string
  circlesHi: string
  label: string
  stroke: string
  contrast: string
  light: boolean
  /** CSS colour for the veil over tenant banner art behind the coins. */
  scrim: string
  /** CSS colour for the drop shadow under a filled stamp coin. */
  coinShadow: string
}

const ESPRESSO = '#0b0a07'
const AMBER_DEFAULT = '#d97706'
const WARM_PAPER = '#f3e9d4'

// Mirrors lightBgLuma / minStrokeGap in palette.go.
const LIGHT_BG_LUMA = 140
const MIN_STROKE_GAP = 42

// Mirrors heroScrimAlpha(Light) / coinShadowAlpha(Light) in passimage.go.
const SCRIM_ALPHA_DARK = 120 / 255
const SCRIM_ALPHA_LIGHT = 140 / 255
const COIN_SHADOW_ALPHA_DARK = 70 / 255
const COIN_SHADOW_ALPHA_LIGHT = 48 / 255

/** Parse a 3- or 6-digit hex. Returns null for anything else, so callers can
 *  fall through to the next source rather than silently rendering black. */
export function parseHex(hex: string): [number, number, number] | null {
  const v = (hex || '').trim().replace(/^#/, '')
  const full = v.length === 3 ? v.split('').map((c) => c + c).join('') : v
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null
  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  ]
}

/** Normalise a valid 3- or 6-digit hex to lowercase 6-digit form, or return
 *  '' when it isn't one. The <input type="color"> and the preview both
 *  require the long form. */
export function normalizeHex(hex: string): string {
  const c = parseHex(hex)
  return c ? toHex(c[0], c[1], c[2]) : ''
}

function toHex(r: number, g: number, b: number): string {
  const c = (n: number) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0')
  return `#${c(r)}${c(g)}${c(b)}`
}

/** Blend a toward b by t (0..1). Mirrors mixRGBA, including its +0.5 rounding
 *  — an off-by-one here shows up as a visibly different swatch in the preview. */
export function mix(a: string, b: string, t: number): string {
  const ca = parseHex(a) ?? [0, 0, 0]
  const cb = parseHex(b) ?? [0, 0, 0]
  return toHex(
    Math.floor(ca[0] * (1 - t) + cb[0] * t + 0.5),
    Math.floor(ca[1] * (1 - t) + cb[1] * t + 0.5),
    Math.floor(ca[2] * (1 - t) + cb[2] * t + 0.5),
  )
}

/** Rec.601 perceived brightness on 0..255. Mirrors luma() in palette.go. */
export function luma(hex: string): number {
  const c = parseHex(hex) ?? [0, 0, 0]
  return 0.299 * c[0] + 0.587 * c[1] + 0.114 * c[2]
}

/** Mirrors ensureBright — used ONLY to reproduce pre-split cards. It is not
 *  part of the live render path any more: once the background became a free
 *  tenant choice, its legibility guarantee was false and its only remaining
 *  effect was bleaching deep brand colours toward peach. */
function ensureBright(hex: string): string {
  const l = luma(hex)
  if (l >= 120) return normalizeHex(hex)
  return mix(hex, '#ffe6b0', Math.min(0.5, (120 - l) / 255))
}

/** The three colours that reproduce, exactly, the card an accent-only tenant
 *  rendered before the split. Mirrors LegacyCardColors in palette.go. */
export function legacyCardColors(accentHex: string): {
  background: string
  text: string
  circles: string
} {
  const acc = parseHex(accentHex) ? accentHex : AMBER_DEFAULT
  return {
    background: mix(ESPRESSO, acc, 0.15),
    text: WARM_PAPER,
    circles: ensureBright(acc),
  }
}

/** Mirrors derivePalette in palette.go, including the per-field fallback to
 *  the legacy derivation when a colour is blank. */
export function deriveCardPalette(cc: CardColors): CardPalette {
  const legacy = legacyCardColors(cc.legacyAccent)
  const pick = (want: string, fallback: string) => normalizeHex(want) || fallback

  const bg = pick(cc.background, legacy.background)
  const text = pick(cc.text, legacy.text)
  const circles = pick(cc.circles, legacy.circles)

  const light = luma(bg) >= LIGHT_BG_LUMA
  const contrast = light ? ESPRESSO : '#ffffff'

  let stroke = circles
  let label = circles
  if (Math.abs(luma(circles) - luma(bg)) < MIN_STROKE_GAP) {
    stroke = contrast
    label = mix(text, bg, 0.28)
  }

  return {
    bg,
    text,
    circles,
    panel: mix(bg, circles, 0.1),
    circlesHi: mix(circles, contrast, 0.34),
    label,
    stroke,
    contrast,
    light,
    scrim: light
      ? `rgba(255,255,255,${SCRIM_ALPHA_LIGHT.toFixed(2)})`
      : `rgba(0,0,0,${SCRIM_ALPHA_DARK.toFixed(2)})`,
    coinShadow: light
      ? `rgba(0,0,0,${COIN_SHADOW_ALPHA_LIGHT.toFixed(2)})`
      : `rgba(0,0,0,${COIN_SHADOW_ALPHA_DARK.toFixed(2)})`,
  }
}

/** WCAG 2.1 relative luminance. Deliberately NOT the Rec.601 luma above:
 *  that one mirrors the Go palette's branch decisions, this one is the
 *  accessibility metric the contrast warning reports against. */
function relativeLuminance(hex: string): number {
  const c = parseHex(hex) ?? [0, 0, 0]
  const chan = (v: number) => {
    const s = v / 255
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
  }
  return 0.2126 * chan(c[0]) + 0.7152 * chan(c[1]) + 0.0722 * chan(c[2])
}

/** WCAG contrast ratio, 1..21. */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a)
  const lb = relativeLuminance(b)
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05)
}

/** WCAG AA for normal text. The card's field values are small, so this is
 *  the right bar for text-on-background. */
export const CONTRAST_TEXT_MIN = 4.5
/** WCAG AA for non-text/graphical objects — the stamp coins. */
export const CONTRAST_GRAPHIC_MIN = 3
